import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  ShieldCheckIcon,
  PlusIcon,
  TrashIcon,
  UserIcon,
  KeyIcon,
  EyeIcon,
  PencilIcon,
  CheckIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { api } from '../services/api';
import toast from '../utils/toast';

const UserPermissionsModal = ({ user, isOpen, onClose, onUpdate, isAdmin = false }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [userPermissions, setUserPermissions] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [showAddRole, setShowAddRole] = useState(false);
  const [showAddPermission, setShowAddPermission] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedPermission, setSelectedPermission] = useState('');

  useEffect(() => {
    if (isOpen && user) {
      fetchUserPermissions();
      fetchAvailableOptions();
    }
  }, [isOpen, user]);

  const fetchUserPermissions = async () => {
    try {
      setLoading(true);
      const response = isAdmin 
        ? await api.getAdminUserPermissions(user.id, localStorage.getItem('admin_token'))
        : await api.getUserPermissions(user.id);
      setUserPermissions(response.data.permissions || {});
    } catch (error) {
      console.error('Error fetching user permissions:', error);
      toast.error('Failed to load user permissions');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableOptions = async () => {
    try {
      const token = isAdmin ? localStorage.getItem('admin_token') : null;
      const [rolesResponse, permissionsResponse] = await Promise.all([
        isAdmin ? api.getAdminRolesWithPermissions(token) : api.getRoles(),
        isAdmin ? api.getAdminPermissions(token) : api.getPermissions()
      ]);
      setAvailableRoles(rolesResponse.data.roles || []);
      setAvailablePermissions(isAdmin ? permissionsResponse.data.permissions || {} : permissionsResponse.data.permissions_list || []);
    } catch (error) {
      console.error('Error fetching available options:', error);
    }
  };

  const assignRole = async () => {
    if (!selectedRole) return;
    
    try {
      setSubmitting(true);
      if (isAdmin) {
        await api.assignAdminUserRole(user.id, { role_name: selectedRole }, localStorage.getItem('admin_token'));
      } else {
        await api.assignUserRole(user.id, { role_name: selectedRole });
      }
      toast.success(`${selectedRole} role assigned successfully`);
      fetchUserPermissions();
      setSelectedRole('');
      setShowAddRole(false);
      onUpdate?.();
    } catch (error) {
      console.error('Error assigning role:', error);
      toast.error('Failed to assign role');
    } finally {
      setSubmitting(false);
    }
  };

  const removeRole = async (roleName) => {
    try {
      setSubmitting(true);
      if (isAdmin) {
        await api.removeAdminUserRole(user.id, roleName, localStorage.getItem('admin_token'));
      } else {
        await api.removeUserRole(user.id, roleName);
      }
      toast.success(`${roleName} role removed successfully`);
      fetchUserPermissions();
      onUpdate?.();
    } catch (error) {
      console.error('Error removing role:', error);
      toast.error('Failed to remove role');
    } finally {
      setSubmitting(false);
    }
  };

  const grantPermission = async () => {
    if (!selectedPermission) return;
    
    try {
      setSubmitting(true);
      if (isAdmin) {
        await api.grantAdminUserPermission(user.id, { permission_name: selectedPermission }, localStorage.getItem('admin_token'));
      } else {
        await api.grantUserPermission(user.id, { permission_name: selectedPermission });
      }
      toast.success('Permission granted successfully');
      fetchUserPermissions();
      setSelectedPermission('');
      setShowAddPermission(false);
    } catch (error) {
      console.error('Error granting permission:', error);
      toast.error('Failed to grant permission');
    } finally {
      setSubmitting(false);
    }
  };

  const revokePermission = async (permissionName) => {
    try {
      setSubmitting(true);
      if (isAdmin) {
        await api.revokeAdminUserPermission(user.id, permissionName, localStorage.getItem('admin_token'));
      } else {
        await api.revokeUserPermission(user.id, permissionName);
      }
      toast.success('Permission revoked successfully');
      fetchUserPermissions();
    } catch (error) {
      console.error('Error revoking permission:', error);
      toast.error('Failed to revoke permission');
    } finally {
      setSubmitting(false);
    }
  };

  const getPermissionsByCategory = () => {
    if (!userPermissions.by_category) return {};
    return userPermissions.by_category;
  };

  const getCurrentRoles = () => {
    if (!userPermissions.role_based) return [];
    const roleNames = [...new Set(userPermissions.role_based.map(p => p.source_role_name))];
    return roleNames.filter(Boolean);
  };

  const getAvailableRolesToAdd = () => {
    const currentRoles = getCurrentRoles();
    return availableRoles.filter(role => !currentRoles.includes(role.name));
  };

  const getAvailablePermissionsToAdd = () => {
    const currentPermissions = userPermissions.role_based || [];
    const directPermissions = userPermissions.direct_grants || [];
    const allCurrent = [...currentPermissions, ...directPermissions].map(p => p.permission_name);
    
    if (isAdmin) {
      // Admin permissions are in object format by category
      const flatPermissions = Object.values(availablePermissions).flat();
      return flatPermissions.filter(perm => !allCurrent.includes(perm.name));
    } else {
      // Regular permissions are in array format
      return availablePermissions.filter(perm => !allCurrent.includes(perm.name));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b">
          <div className="flex items-center space-x-3">
            <UserIcon className="h-6 w-6 text-indigo-600" />
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                Manage Permissions: {user?.first_name} {user?.last_name}
              </h3>
              <p className="text-sm text-gray-500">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : (
          <div className="mt-6 space-y-6">
            {/* Roles Section */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-md font-medium text-gray-900 flex items-center">
                  <ShieldCheckIcon className="h-5 w-5 mr-2 text-indigo-600" />
                  Assigned Roles ({getCurrentRoles().length})
                </h4>
                <button
                  onClick={() => setShowAddRole(!showAddRole)}
                  className="btn btn-secondary btn-sm"
                  disabled={submitting}
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  Add Role
                </button>
              </div>

              {/* Add Role Form */}
              {showAddRole && (
                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                  <div className="flex items-center space-x-3">
                    <select
                      value={selectedRole}
                      onChange={(e) => setSelectedRole(e.target.value)}
                      className="form-select flex-1"
                    >
                      <option value="">Select a role...</option>
                      {getAvailableRolesToAdd().map(role => (
                        <option key={role.id} value={role.name}>
                          {role.display_name} ({role.name})
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={assignRole}
                      disabled={!selectedRole || submitting}
                      className="btn btn-primary btn-sm"
                    >
                      <CheckIcon className="h-4 w-4 mr-1" />
                      Assign
                    </button>
                    <button
                      onClick={() => setShowAddRole(false)}
                      className="btn btn-secondary btn-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Current Roles */}
              <div className="space-y-2">
                {getCurrentRoles().map(roleName => (
                  <div key={roleName} className="flex items-center justify-between bg-indigo-50 p-3 rounded-lg">
                    <div className="flex items-center">
                      <ShieldCheckIcon className="h-5 w-5 text-indigo-600 mr-2" />
                      <span className="font-medium text-indigo-900">{roleName}</span>
                      <span className="ml-2 text-sm text-indigo-600">
                        ({userPermissions.role_based?.filter(p => p.source_role_name === roleName).length} permissions)
                      </span>
                    </div>
                    <button
                      onClick={() => removeRole(roleName)}
                      disabled={submitting}
                      className="text-red-600 hover:text-red-800"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                {getCurrentRoles().length === 0 && (
                  <p className="text-gray-500 text-sm italic">No roles assigned</p>
                )}
              </div>
            </div>

            {/* Direct Permissions Section */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-md font-medium text-gray-900 flex items-center">
                  <KeyIcon className="h-5 w-5 mr-2 text-green-600" />
                  Direct Permissions ({userPermissions.direct_grants?.length || 0})
                </h4>
                <button
                  onClick={() => setShowAddPermission(!showAddPermission)}
                  className="btn btn-secondary btn-sm"
                  disabled={submitting}
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  Add Permission
                </button>
              </div>

              {/* Add Permission Form */}
              {showAddPermission && (
                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                  <div className="flex items-center space-x-3">
                    <select
                      value={selectedPermission}
                      onChange={(e) => setSelectedPermission(e.target.value)}
                      className="form-select flex-1"
                    >
                      <option value="">Select a permission...</option>
                      {getAvailablePermissionsToAdd().map(perm => (
                        <option key={perm.id} value={perm.name}>
                          {perm.display_name} ({perm.name})
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={grantPermission}
                      disabled={!selectedPermission || submitting}
                      className="btn btn-primary btn-sm"
                    >
                      <CheckIcon className="h-4 w-4 mr-1" />
                      Grant
                    </button>
                    <button
                      onClick={() => setShowAddPermission(false)}
                      className="btn btn-secondary btn-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Direct Permissions List */}
              <div className="space-y-2">
                {userPermissions.direct_grants?.map(permission => (
                  <div key={permission.permission_name} className="flex items-center justify-between bg-green-50 p-3 rounded-lg">
                    <div className="flex items-center">
                      <KeyIcon className="h-5 w-5 text-green-600 mr-2" />
                      <span className="font-medium text-green-900">{permission.permission_display_name}</span>
                      <span className="ml-2 text-sm text-green-600">({permission.permission_name})</span>
                    </div>
                    <button
                      onClick={() => revokePermission(permission.permission_name)}
                      disabled={submitting}
                      className="text-red-600 hover:text-red-800"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                {(!userPermissions.direct_grants || userPermissions.direct_grants.length === 0) && (
                  <p className="text-gray-500 text-sm italic">No direct permissions granted</p>
                )}
              </div>
            </div>

            {/* All Permissions by Category */}
            <div>
              <h4 className="text-md font-medium text-gray-900 flex items-center mb-4">
                <EyeIcon className="h-5 w-5 mr-2 text-gray-600" />
                All Permissions by Category ({userPermissions.total_count || 0})
              </h4>
              
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {Object.entries(getPermissionsByCategory()).map(([category, permissions]) => (
                  <div key={category} className="border rounded-lg p-4">
                    <h5 className="font-medium text-gray-900 mb-2">{category} ({permissions.length})</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {permissions.map(permission => (
                        <div key={permission.permission_name} className="flex items-center justify-between text-sm">
                          <span className="text-gray-700">{permission.permission_display_name}</span>
                          <div className="flex items-center space-x-2">
                            {permission.permission_source === 'role' && (
                              <span className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded text-xs">
                                {permission.source_role_name}
                              </span>
                            )}
                            {permission.permission_source === 'direct' && (
                              <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                                Direct
                              </span>
                            )}
                            <button
                              onClick={() => revokePermission(permission.permission_name)}
                              disabled={submitting}
                              className="text-red-600 hover:text-red-800"
                              title="Remove this permission"
                            >
                              <TrashIcon className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                {Object.keys(getPermissionsByCategory()).length === 0 && (
                  <p className="text-gray-500 text-sm italic">No permissions found</p>
                )}
              </div>
            </div>

            {/* Warning Message */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-2" />
                <div className="text-sm text-yellow-700">
                  <p><strong>Permission Management:</strong></p>
                  <ul className="mt-1 space-y-1">
                    <li>• Assigning a role copies ALL role permissions to the user</li>
                    <li>• Removing a role deletes ALL permissions from that role</li>
                    <li>• Direct permissions are independent of roles</li>
                    <li>• You can remove individual permissions even if they came from a role</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="btn btn-secondary"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserPermissionsModal;